/**
 * FeedbackGlobalTriggers - 앱 전역 즉시 캡처 트리거
 *
 * App.tsx에서 렌더링되어 앱 전역에서 피드백 캡처를 가능하게 합니다.
 * - ⌨️ Ctrl+Shift+K (Mac: ⌘+Shift+K) → 캡처 → 프리뷰 모달
 * - ⌨️ Ctrl+Shift+A (Mac: ⌘+Shift+A) → 캡처 → 영역 선택 → 프리뷰 모달
 * - 📱 Shake to Report (모바일) → 캡처 → 프리뷰 모달
 * - 🛠️ 디버깅 모드 (localStorage) → 플로팅 캡처 버튼 표시
 *
 * Draft = 경량 이미지(draftImages 컬렉션)
 * 피드백 제출 시 첨부된 Draft 이미지는 자동 삭제됩니다.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import hotkeys from 'hotkeys-js';
import { screenCapture } from '../../services/ScreenCaptureService';
import { FeedbackFormModal } from './FeedbackFormModal';
import { FeedbackDraftImagesModal } from './FeedbackDraftImagesModal';
import { FeedbackCaptureModal } from './FeedbackCaptureModal';
import { ScreenshotCapture } from './ScreenshotCapture';
import { ScreenshotFloatingButton } from './ScreenshotFloatingButton';
import { My } from '../../services/My';
import { Fire } from '../../services/FireClient';
import { Capacitor } from '@capacitor/core';
import type { DraftImage } from '../../models/Feedback';
import { collectAll } from './FeedbackEnvironment';
import { useToast } from '../../components/ui/use-toast';
import { t } from '@lingui/macro';
import { useAppUIContext } from '../../context/AppUIContext';
import { Uid } from '../../models/User';
import { ScreenshotMaskingEditor } from './ScreenshotMaskingEditor';
import { QaFeedbackFormModal } from './QaFeedbackFormModal';
import {
  IS_QA_ELIGIBLE, isQaModeEnabled, subscribeQaMode,
  QA_CAPTURE_EVENT, QA_AREA_CAPTURE_EVENT,
} from '../../config/qaFeedback';
import type { FeedbackEnvironment, FeedbackDiagnostics } from '../../models/Feedback';

// Shake 감지 임계치 (acceleration 기반, 중력 제외)
const SHAKE_ACCEL_THRESHOLD = 20;  // m/s² 단위, 의도적 흔들기만 감지
const SHAKE_COUNT_NEEDED = 3;      // 연속 3회 이상 흔들기 필요
const SHAKE_WINDOW_MS = 1500;      // 1.5초 내에 3회 이상
const SHAKE_COOLDOWN_MS = 3000;    // 트리거 후 3초 쿨다운

type Phase = 'idle' | 'area-select' | 'capture_preview' | 'form' | 'draft_images' | 'qa-annotate' | 'qa-form';

const DEBUG_MODE_KEY = 'feedback-debug-mode';

export const FeedbackGlobalTriggers: React.FC = () => {
  const { toast } = useToast();
  const { activeWorkspaceId, workspaces } = useAppUIContext();
  const [phase, setPhase] = useState<Phase>('idle');
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [preAttachedScreenshots, setPreAttachedScreenshots] = useState<Blob[]>([]);
  const lastShakeRef = useRef(0);
  const triggerCaptureRef = useRef<() => void>(() => {});
  const triggerAreaCaptureRef = useRef<() => void>(() => {});
  const isSavingRef = useRef(false);

  // ── QA 피드백 모드 (dev 빌드 전용, IS_QA_ELIGIBLE 로 운영 빌드에서 DCE) ──
  const [qaMode, setQaMode] = useState(() => IS_QA_ELIGIBLE && isQaModeEnabled());
  const qaModeRef = useRef(qaMode);
  useEffect(() => { qaModeRef.current = qaMode; }, [qaMode]);
  const [qaAnnotatedBlob, setQaAnnotatedBlob] = useState<Blob | null>(null);
  const [qaEnv, setQaEnv] = useState<FeedbackEnvironment | null>(null);
  const [qaDiag, setQaDiag] = useState<FeedbackDiagnostics | null>(null);
  const [debugMode, setDebugMode] = useState(() =>
    localStorage.getItem(DEBUG_MODE_KEY) === 'true'
  );
  const [formOpen, setFormOpen] = useState(false);
  const [attachedDraftImages, setAttachedDraftImages] = useState<DraftImage[]>([]);
  const [draftImageCount, setDraftImageCount] = useState(0);

  // localStorage 변경 감지
  useEffect(() => {
    const handleStorage = () => {
      setDebugMode(localStorage.getItem(DEBUG_MODE_KEY) === 'true');
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('debug-mode-changed', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('debug-mode-changed', handleStorage);
    };
  }, []);

  // Draft 이미지 카운트 구독
  useEffect(() => {
    if (!My.isLoggedIn()) return;
    const unsub = Fire.feedback.subscribeDraftImages(My.user.id, (images) => {
      setDraftImageCount(images.length);
    });
    return () => unsub();
  }, []);

  // ── 즉시 캡처 → 캡처 프리뷰 모달 표시 ──
  const triggerCapture = useCallback(async () => {
    if (!My.isLoggedIn()) return;
    if (phase !== 'idle') return;
    if (isSavingRef.current) return;

    try {
      const blob = await screenCapture.captureScreen();
      setCapturedBlob(blob);
      // QA 모드: 캡처 후 크롭 단계(area-select)로 — 영역 선택 or "전체 사용" 후
      //          handleAreaCaptured 가 진단 스냅샷 + 주석 편집기로 넘긴다.
      if (IS_QA_ELIGIBLE && qaModeRef.current) {
        setPhase('area-select');
      } else {
        setPhase('capture_preview');
      }
    } catch (err) {
      console.error('FeedbackGlobalTriggers: capture failed', err);
    }
  }, [phase]);

  // ref에 최신 triggerCapture 저장 (shake / 배지 이벤트 useEffect 안정화)
  useEffect(() => {
    triggerCaptureRef.current = triggerCapture;
  }, [triggerCapture]);

  // ── QA 모드 변경 구독 (배지 팝오버 ↔ 트리거 동기화) ──
  useEffect(() => {
    if (!IS_QA_ELIGIBLE) return;
    return subscribeQaMode(setQaMode);
  }, []);

  // ── 배지 팝오버 → 캡처 요청 이벤트 수신 ──
  useEffect(() => {
    if (!IS_QA_ELIGIBLE) return;
    const onCapture = () => triggerCaptureRef.current();
    const onAreaCapture = () => triggerAreaCaptureRef.current();
    window.addEventListener(QA_CAPTURE_EVENT, onCapture);
    window.addEventListener(QA_AREA_CAPTURE_EVENT, onAreaCapture);
    return () => {
      window.removeEventListener(QA_CAPTURE_EVENT, onCapture);
      window.removeEventListener(QA_AREA_CAPTURE_EVENT, onAreaCapture);
    };
  }, []);

  // ── 영역 선택 캡처 ──
  const triggerAreaCapture = useCallback(async () => {
    if (!My.isLoggedIn()) return;
    if (phase !== 'idle') return;
    try {
      const blob = await screenCapture.captureScreen();
      setCapturedBlob(blob);
      setPhase('area-select');
    } catch (err) {
      console.error('FeedbackGlobalTriggers: area capture failed', err);
    }
  }, [phase]);

  useEffect(() => {
    triggerAreaCaptureRef.current = triggerAreaCapture;
  }, [triggerAreaCapture]);

  // ── 영역 선택 완료 → (QA) 주석 편집기 / (일반) 캡처 프리뷰 ──
  const handleAreaCaptured = useCallback(async (croppedBlob: Blob) => {
    setCapturedBlob(croppedBlob);
    if (IS_QA_ELIGIBLE && qaModeRef.current) {
      const snap = collectAll();
      setQaEnv(snap.environment);
      setQaDiag(snap.diagnostics);
      setPhase('qa-annotate');
    } else {
      setPhase('capture_preview');
    }
  }, []);

  // ── QA: 주석 완료 → 제출 폼 ──
  const handleQaAnnotated = useCallback((annotatedBlob: Blob) => {
    setQaAnnotatedBlob(annotatedBlob);
    setPhase('qa-form');
  }, []);

  // ── QA: 흐름 종료(취소/제출 후) ──
  const handleQaClose = useCallback(() => {
    setPhase('idle');
    setCapturedBlob(null);
    setQaAnnotatedBlob(null);
    setQaEnv(null);
    setQaDiag(null);
  }, []);

  // ── 캡처 프리뷰 → Draft 이미지로 저장 ──
  const handleSaveDraftFromCapture = useCallback(async () => {
    if (!capturedBlob || isSavingRef.current) return;
    isSavingRef.current = true;
    try {
      const draftId = Uid.get();
      const platform = Capacitor.isNativePlatform()
        ? Capacitor.getPlatform()
        : 'web';

      // Storage에 업로드
      const imageUrl = await Fire.feedback.uploadDraftScreenshot(capturedBlob, draftId);

      // 환경정보 + 진단데이터 스냅샷
      const { environment, diagnostics } = collectAll();

      // DraftImage 문서 저장
      const draftImage: DraftImage = {
        id: draftId,
        userId: My.user.id,
        imageUrl,
        capturedAt: new Date(),
        platform,
        currentUrl: window.location.href,
        environment,
        diagnostics,
      };
      await Fire.feedback.saveDraftImage(draftImage);

      toast({
        title: `📸 ${t`Screenshot saved`}`,
        description: t`You can add details later from any device`,
        duration: 2000,
      });
      setCapturedBlob(null);
      setPhase('idle');
    } catch (err) {
      console.error('FeedbackGlobalTriggers: draft image save failed', err);
      toast({ variant: 'destructive', title: t`Failed to save screenshot` });
    } finally {
      isSavingRef.current = false;
    }
  }, [capturedBlob, toast]);

  // ── 캡처 프리뷰 → 설명 추가(바로 폼) ──
  const handleAddDetailsFromCapture = useCallback(() => {
    if (!capturedBlob) return;
    setPreAttachedScreenshots([capturedBlob]);
    setAttachedDraftImages([]);
    setPhase('form');
  }, [capturedBlob]);

  // ── 취소 ──
  const handleCancel = useCallback(() => {
    setCapturedBlob(null);
    setPhase('idle');
  }, []);

  // ── Draft 이미지 목록에서 "새 피드백 작성" ──
  const handleCreateFromDrafts = useCallback((selectedImages: DraftImage[]) => {
    setAttachedDraftImages(selectedImages);
    setPreAttachedScreenshots([]);
    setPhase('form');
  }, []);

  // ── 키보드 단축키: ⌘+Shift+K (즉시 캡처) ──
  useEffect(() => {
    hotkeys('ctrl+shift+k,command+shift+k', (e) => {
      e.preventDefault();
      triggerCapture();
    });
    return () => { hotkeys.unbind('ctrl+shift+k,command+shift+k'); };
  }, [triggerCapture]);

  // ── 키보드 단축키: ⌘+Shift+A (영역 선택 캡처) ──
  useEffect(() => {
    hotkeys('ctrl+shift+a,command+shift+a', (e) => {
      e.preventDefault();
      triggerAreaCapture();
    });
    return () => { hotkeys.unbind('ctrl+shift+a,command+shift+a'); };
  }, [triggerAreaCapture]);

  // ── Shake to Report (모바일 전용, 명시적 활성화 필요) ──
  useEffect(() => {
    if (!debugMode) return;
    if (!Capacitor.isNativePlatform() && !navigator.maxTouchPoints) return;

    let motionListenerAdded = false;
    const shakeTimestamps: number[] = [];

    const handleMotion = (e: DeviceMotionEvent) => {
      const acc = e.acceleration;
      if (!acc || acc.x == null || acc.y == null || acc.z == null) return;

      const magnitude = Math.sqrt(acc.x * acc.x + acc.y * acc.y + acc.z * acc.z);

      if (magnitude > SHAKE_ACCEL_THRESHOLD) {
        const now = Date.now();
        while (shakeTimestamps.length > 0 && now - shakeTimestamps[0] > SHAKE_WINDOW_MS) {
          shakeTimestamps.shift();
        }
        shakeTimestamps.push(now);

        const timeSinceLast = now - lastShakeRef.current;
        if (shakeTimestamps.length >= SHAKE_COUNT_NEEDED && timeSinceLast > SHAKE_COOLDOWN_MS) {
          lastShakeRef.current = now;
          shakeTimestamps.length = 0;
          triggerCaptureRef.current();
        }
      }
    };

    const addMotionListener = () => {
      if (motionListenerAdded) return;
      motionListenerAdded = true;
      window.addEventListener('devicemotion', handleMotion);
    };

    const removeMotionListener = () => {
      window.removeEventListener('devicemotion', handleMotion);
      motionListenerAdded = false;
    };

    const handleShakeEnabled = () => addMotionListener();
    const handleShakeDisabled = () => removeMotionListener();
    window.addEventListener('shake-enabled', handleShakeEnabled);
    window.addEventListener('shake-disabled', handleShakeDisabled);
    window.addEventListener('motion-permission-granted', handleShakeEnabled);

    return () => {
      removeMotionListener();
      window.removeEventListener('shake-enabled', handleShakeEnabled);
      window.removeEventListener('shake-disabled', handleShakeDisabled);
      window.removeEventListener('motion-permission-granted', handleShakeEnabled);
    };
  }, [debugMode]);

  return (
    <>
      {/* 영역 선택 */}
      {phase === 'area-select' && capturedBlob && (
        <ScreenshotCapture
          fullScreenBlob={capturedBlob}
          onCapture={handleAreaCaptured}
          onCancel={handleCancel}
        />
      )}

      {/* QA: 주석 편집기 (전체 도구) */}
      {IS_QA_ELIGIBLE && phase === 'qa-annotate' && capturedBlob && (
        <ScreenshotMaskingEditor
          imageBlob={capturedBlob}
          mode="annotate"
          onConfirm={handleQaAnnotated}
          onCancel={handleQaClose}
        />
      )}

      {/* QA: 제출 폼 → 지정 채널 Tasks 카드 */}
      {IS_QA_ELIGIBLE && phase === 'qa-form' && qaEnv && qaDiag && (
        <QaFeedbackFormModal
          open={true}
          annotatedBlob={qaAnnotatedBlob}
          environment={qaEnv}
          diagnostics={qaDiag}
          onClose={handleQaClose}
          onSubmitted={handleQaClose}
        />
      )}

      {/* 캡처 미리보기 (임시저장/설명추가 선택) */}
      {phase === 'capture_preview' && (
        <FeedbackCaptureModal
          open={true}
          blob={capturedBlob}
          onCancel={handleCancel}
          onSaveDraft={handleSaveDraftFromCapture}
          onAddDetails={handleAddDetailsFromCapture}
          saving={isSavingRef.current}
        />
      )}

      {/* 피드백 폼 */}
      {phase === 'form' && (
        <FeedbackFormModal
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              setPhase('idle');
              setAttachedDraftImages([]);
              setPreAttachedScreenshots([]);
            }
          }}
          defaultType="bug"
          preAttachedScreenshots={preAttachedScreenshots}
          preAttachedDraftImages={attachedDraftImages}
        />
      )}

      {/* Draft 이미지 목록 */}
      {phase === 'draft_images' && (
        <FeedbackDraftImagesModal
          open={true}
          onOpenChange={(open: boolean) => { if (!open) setPhase('idle'); }}
          onCreateFeedback={handleCreateFromDrafts}
        />
      )}

      {/* 디버깅 모드: 플로팅 캡처 버튼 */}
      {debugMode && (
        <ScreenshotFloatingButton
          draftCount={draftImageCount}
          onDraftsOpen={() => setPhase('draft_images')}
          onTriggerCapture={triggerCapture}
          onTriggerAreaCapture={triggerAreaCapture}
          onClose={() => {
            setDebugMode(false);
            localStorage.setItem(DEBUG_MODE_KEY, 'false');
            window.dispatchEvent(new Event('debug-mode-changed'));
          }}
        />
      )}
    </>
  );
};
